// BrandDetail — v1 "Portfolio (refined)" redesign.
// Cover backdrop + identity header, two-column body. Left: portfolio mosaic,
// About paragraph, full-width upcoming-event rows, text-only past-events list.
// Right sidebar: Quick facts, Where to find them, Relevant links, and (for
// promoters) an invite CTA. The identity band is the shared DetailHeader
// (macro category chips included); CustomMediaModal is the portfolio lightbox.
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PuffLoader } from 'react-spinners';
import {
  MapPinIcon,
  StarIcon,
  ArrowRightIcon,
  LanguageIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';

import {
  getBrandBySlug,
  listBrandPaidEvents,
} from '../../redux/actions/brands';
import { inviteBrandToEvent } from '../../redux/actions/events';

import getEventDateRange, { getTodayDateOnly } from 'utils/eventDateRange';
import { isEventCancelled } from 'utils/eventStatus';
import { useAlert } from 'context/AlertContext';

import CategoryAccordion from 'components/CategoryAccordion';
import CategorySelectorGrid from 'components/CategorySelectorGrid';
import InviteMenu from 'components/InviteMenu';
import CustomMediaModal from 'components/CustomMediaModal';
import ModalDialog from 'components/DialogModal';
import DetailHeader, { DetailPill } from 'components/DetailHeader';
import usePageMeta from 'hooks/usePageMeta';

import {
  DTSocials,
  SectionHeader,
  SectionActionLink,
  SidebarCard,
  BrandUpcomingEventRow,
  BrandPastEventRow,
  WebsitePill,
} from 'components/BrandDetail/atoms';

// Both event lists and the portfolio mosaic keep the brand page short: only
// the first few entries stay inline, the rest live behind the section's popup.
const EVENT_PREVIEW_LIMIT = 3;
const PORTFOLIO_TILES = 6;

const PreservedText = ({ text }) => (
  <div className="whitespace-pre-wrap break-words leading-relaxed text-gray-800">
    {text || ''}
  </div>
);

const sortByStartAsc = (a, b) => {
  const ra = getEventDateRange(a.date);
  const rb = getEventDateRange(b.date);
  if (!ra && !rb) return 0;
  if (!ra) return 1;
  if (!rb) return -1;
  return ra.startDate.localeCompare(rb.startDate);
};

const sortByStartDesc = (a, b) => -sortByStartAsc(a, b);

const BrandDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { brandSlug } = useParams();
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();

  const [viewingOriginal, setViewingOriginal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [modalContent, setModalContent] = useState({
    content: '',
    title: '',
    size: 'sm',
  });
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaStartIndex, setMediaStartIndex] = useState(0);

  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { brand, loading } = useSelector((state) => state.brands);

  const {
    id,
    avatar,
    username,
    socials,
    city,
    media,
    country,
    categories,
    foundingYear,
    description,
    longDescription,
  } = brand || {};

  const brandLoaded = brand?.slug === brandSlug;
  usePageMeta({
    title: brandLoaded ? username : undefined,
    description: brandLoaded ? description : undefined,
  });

  const isPromoter = user?.role === 'promoter';

  const { paidEvents, paidEventsLoading, paidEventsError } = useSelector(
    (state) => state.brands
  );

  // Split paid events into upcoming/past based on the end of the range.
  // Two records never reach the upcoming list whatever their dates say: a
  // cancelled event is out of circulation platform-wide (T2B-420) and so is
  // dropped from the track record too, and a "Completed" one is over by
  // definition — that keeps an event whose stored dates outlived it from
  // being advertised as still to come.
  const { upcomingEvents, pastEvents } = useMemo(() => {
    const todayYmd = getTodayDateOnly();
    const upcoming = [];
    const past = [];
    (paidEvents || []).forEach((ev) => {
      const status = String(ev?.status || '').toLowerCase();
      if (isEventCancelled(ev) || status === 'rejected') return;
      const range = getEventDateRange(ev?.date);
      if (!range) return;
      if (status !== 'completed' && range.endDate >= todayYmd) upcoming.push(ev);
      else past.push(ev);
    });
    return {
      upcomingEvents: upcoming.sort(sortByStartAsc),
      pastEvents: past.sort(sortByStartDesc),
    };
  }, [paidEvents]);

  const fetchLang = viewingOriginal ? undefined : i18n.language;

  useEffect(() => {
    if (brandSlug) {
      dispatch(getBrandBySlug(brandSlug, { lang: fetchLang })).then(
        (brandData) => {
          if (brandData?.id) {
            dispatch(listBrandPaidEvents(brandData.id));
          }
        }
      );
    }
  }, [dispatch, brandSlug, fetchLang]);

  // Media for the lightbox: avatar (if any) + portfolio media.
  const portfolioUrls = useMemo(
    () =>
      (media ?? [])
        .map((m) => (typeof m === 'string' ? m : m?.url || m?.src || m?.path))
        .filter(Boolean),
    [media]
  );

  const imagesForModal = useMemo(() => {
    const ordered = avatar ? [avatar, ...portfolioUrls] : portfolioUrls;
    const seen = new Set();
    return ordered.filter((url) => {
      if (!url || seen.has(url)) return false;
      seen.add(url);
      return true;
    });
  }, [avatar, portfolioUrls]);

  const openMediaAt = (idx) => {
    if (!imagesForModal.length) return;
    setMediaStartIndex(idx);
    setShowMediaModal(true);
  };

  const handleShowMoreModal = (content, title, size = 'sm') => {
    setModalContent({ content, title, size });
    setShowInfoModal(true);
  };

  const handleCloseMoreModal = () => {
    setShowInfoModal(false);
    setModalContent({ content: '', title: '', size: 'sm' });
  };

  const handleInvite = async (event) => {
    try {
      await dispatch(inviteBrandToEvent(event.id, id));
      showAlert({
        title: t('brand_detail.invite_success_title'),
        message: t('brand_detail.invite_success_message'),
        primaryAction: {
          label: t('brand_detail.invite_success_primary_action'),
        },
      });
    } catch (e) {
      showAlert({
        title: t('brand_detail.invite_error_title'),
        message:
          e?.response?.data?.message || t('brand_detail.invite_error_message'),
        primaryAction: { label: t('brand_detail.invite_error_primary_action') },
      });
    }
  };

  if (loading || !brand) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4">
        <PuffLoader size={50} color="#f59e0b" />
      </div>
    );
  }

  const relevantLinks = socials?.others ?? [];
  const mediaCount = portfolioUrls.length;

  // ───────── HEADER (identity) ─────────
  const hasSocials =
    socials && Object.keys(socials).some((k) => socials[k] && k !== 'others');
  const renderHeader = () => (
    <DetailHeader
      backLabel={t('brand_detail.back_to_brands')}
      onBack={() => navigate('/listings?searchType=brands')}
      typeLabel={t('common.brand')}
      tone="brand"
      title={username}
      avatar={
        <button
          type="button"
          onClick={() => avatar && openMediaAt(0)}
          className={`w-24 h-24 sm:w-[140px] sm:h-[140px] rounded-3xl overflow-hidden ring-1 ring-line shrink-0 bg-white flex items-center justify-center ${
            avatar ? 'cursor-zoom-in' : ''
          }`}
          title={avatar ? t('brand_detail.open_lightbox') : undefined}
        >
          {avatar ? (
            <img
              src={avatar}
              alt={username}
              className="w-full h-full object-cover"
            />
          ) : (
            <UserCircleIcon className="text-gray-400 w-20 h-20" />
          )}
        </button>
      }
      pills={
        Boolean(city || country || foundingYear) && (
          <>
            {(city || country) && (
              <DetailPill icon={MapPinIcon}>
                {[city, country].filter(Boolean).join(', ')}
              </DetailPill>
            )}
            {foundingYear && (
              <DetailPill icon={StarIcon}>
                {t('brand_detail.established', { year: foundingYear })}
              </DetailPill>
            )}
          </>
        )
      }
      categories={categories}
      aside={
        <>
          <DTSocials socials={socials} size={38} />
          {isPromoter && (
            <>
              {hasSocials && <div className="w-px h-7 bg-line mx-1" />}
              <InviteMenu
                onInvite={handleInvite}
                triggerLabel={t('brand_detail.invite_action')}
                triggerIcon={<PlusIcon className="w-3.5 h-3.5" />}
                triggerClassName="inline-flex items-center justify-between gap-2 px-3.5 h-10 rounded-full text-[12.5px] font-bold transition bg-yellow-03 text-white hover:bg-yellow-05 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              />
            </>
          )}
        </>
      }
    />
  );

  // ───────── PORTFOLIO MOSAIC ─────────
  // The mosaic is always the same shape — one hero plus five tiles — so every
  // brand page reads alike; anything past the sixth image is counted on the
  // bottom-right tile and seen in the lightbox.
  const renderPortfolio = () => {
    if (!mediaCount) return null;
    const heroOffset = avatar ? 1 : 0; // Lightbox index offset because avatar prepends.
    const tiles = portfolioUrls.slice(1, PORTFOLIO_TILES);
    const overflow = mediaCount - PORTFOLIO_TILES;
    return (
      <section>
        <SectionHeader
          eyebrow={t('brand_detail.portfolio_eyebrow')}
          title={t('brand_detail.portfolio_title')}
          action={
            <SectionActionLink
              label={t('brand_detail.see_all')}
              onClick={() => openMediaAt(heroOffset)}
            />
          }
        />
        <div className="grid grid-cols-3 gap-2.5 auto-rows-[100px] sm:auto-rows-[150px]">
          <button
            type="button"
            onClick={() => openMediaAt(heroOffset)}
            className="col-span-2 row-span-2 rounded-2xl overflow-hidden ring-1 ring-line bg-paper"
          >
            <img
              src={portfolioUrls[0]}
              alt=""
              className="w-full h-full object-cover"
            />
          </button>
          {tiles.map((src, i) => {
            const isOverflowTile = overflow > 0 && i === tiles.length - 1;
            return (
              <button
                key={src + i}
                type="button"
                onClick={() => openMediaAt(heroOffset + i + 1)}
                className="rounded-2xl overflow-hidden ring-1 ring-line relative bg-paper"
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
                {isOverflowTile && (
                  <div className="absolute inset-0 bg-ink-strong/60 grid place-items-center">
                    <span className="text-white text-[18px] font-extrabold">
                      +{overflow}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  // ───────── ABOUT ─────────
  const renderAbout = () => {
    if (!description && !longDescription) return null;
    return (
      <section>
        <SectionHeader
          eyebrow={t('brand_detail.about_eyebrow')}
          title={t('brand_detail.about_title')}
          action={
            longDescription ? (
              <SectionActionLink
                label={t('brand_detail.read_full_bio')}
                onClick={() =>
                  handleShowMoreModal(
                    <PreservedText text={longDescription} />,
                    t('brand_detail.about_title')
                  )
                }
              />
            ) : null
          }
        />
        {description && (
          <p className="text-[14px] text-gray-700 leading-[1.75]">
            {description}
          </p>
        )}
      </section>
    );
  };

  // ───────── CATEGORIES ─────────
  const hasSubcategories =
    Array.isArray(categories) &&
    categories.some((c) => {
      const micros = c?.microCategory ?? c?.microCategoryIds;
      return Array.isArray(micros) && micros.length > 0;
    });

  const renderCategories = () => {
    if (!Array.isArray(categories) || categories.length === 0) return null;
    return (
      <section>
        <SectionHeader
          eyebrow={t('brand_detail.categories')}
          title={t('brand_detail.categories_title')}
          action={
            hasSubcategories ? (
              <SectionActionLink
                label={t('brand_detail.see_all')}
                onClick={() =>
                  handleShowMoreModal(
                    <CategoryAccordion categories={categories} />,
                    t('brand_detail.categories')
                  )
                }
              />
            ) : null
          }
        />
        <CategorySelectorGrid
          key={`cats-${id}`}
          noTitle
          initialCategories={categories}
          restrictToSelected
          readOnly
          showMicros={false}
          white
        />
      </section>
    );
  };

  // ───────── UPCOMING EVENTS ─────────
  // Only the next three events stay on the page; the header link opens the
  // full list in a popup so a busy brand does not push the rest of the page
  // off the screen.
  const renderUpcomingList = (events) => (
    <div className="space-y-3">
      {events.map((ev) => (
        <BrandUpcomingEventRow
          key={ev.id}
          event={ev}
          isAuthenticated={isAuthenticated}
        />
      ))}
    </div>
  );

  const renderUpcoming = () => {
    if (paidEventsLoading) {
      return (
        <div className="py-6 flex justify-center">
          <PuffLoader size={50} color="#f59e0b" />
        </div>
      );
    }
    if (paidEventsError) {
      return <div className="py-4 text-red-500 text-sm">{paidEventsError}</div>;
    }
    if (!upcomingEvents.length) {
      return (
        <section>
          <SectionHeader
            eyebrow={t('brand_detail.upcoming_eyebrow')}
            title={t('brand_detail.upcoming_title')}
          />
          <div className="text-gray-500 text-center italic py-6 rounded-3xl bg-white ring-1 ring-line">
            {t('brand_detail.no_upcoming_events')}
          </div>
        </section>
      );
    }
    return (
      <section>
        <SectionHeader
          eyebrow={t('brand_detail.upcoming_eyebrow')}
          title={t('brand_detail.upcoming_title')}
          action={
            <SectionActionLink
              label={t('brand_detail.see_count', {
                count: upcomingEvents.length,
              })}
              onClick={() =>
                handleShowMoreModal(
                  renderUpcomingList(upcomingEvents),
                  t('brand_detail.upcoming_eyebrow'),
                  'md'
                )
              }
            />
          }
        />
        {renderUpcomingList(upcomingEvents.slice(0, EVENT_PREVIEW_LIMIT))}
      </section>
    );
  };

  // ───────── PAST EVENTS (text-only) ─────────
  const renderPastList = (events) => (
    <div className="rounded-3xl bg-white ring-1 ring-line shadow-card divide-y divide-line overflow-hidden">
      {events.map((p) => (
        <BrandPastEventRow key={p.id} event={p} />
      ))}
    </div>
  );

  const renderPast = () => {
    if (!pastEvents.length) return null;
    return (
      <section>
        <SectionHeader
          eyebrow={t('brand_detail.track_record_eyebrow')}
          title={t('brand_detail.past_events_count', {
            count: pastEvents.length,
          })}
          titleClassName="text-[16px]"
          action={
            <SectionActionLink
              label={t('brand_detail.see_count', { count: pastEvents.length })}
              onClick={() =>
                handleShowMoreModal(
                  renderPastList(pastEvents),
                  t('brand_detail.track_record_eyebrow'),
                  'md'
                )
              }
            />
          }
        />
        {renderPastList(pastEvents.slice(0, EVENT_PREVIEW_LIMIT))}
      </section>
    );
  };

  // ───────── SIDEBAR ─────────
  const renderSidebar = () => (
    <aside className="space-y-4">
      <SidebarCard eyebrow={t('brand_detail.quick_facts')}>
        <dl className="mt-3 divide-y divide-line">
          {[
            (city || country) && {
              label: t('brand_detail.fact_based_in'),
              value: [city, country].filter(Boolean).join(', '),
            },
            foundingYear && {
              label: t('brand_detail.fact_founded'),
              value: String(foundingYear),
            },
            {
              label: t('brand_detail.fact_events_confirmed'),
              value: t('brand_detail.events_upcoming', {
                count: upcomingEvents.length,
              }),
            },
          ]
            .filter(Boolean)
            .map((row, i) => (
              <div
                key={i}
                className="py-2.5 flex items-center justify-between gap-3 text-[12.5px]"
              >
                <span className="text-gray-500">{row.label}</span>
                <span className="font-bold text-ink-strong text-right break-words [overflow-wrap:anywhere]">
                  {row.value}
                </span>
              </div>
            ))}
        </dl>
      </SidebarCard>

      {(socials?.website ||
        socials?.instagram ||
        socials?.facebook ||
        socials?.tiktok ||
        socials?.twitter ||
        socials?.linkedin) && (
        <SidebarCard eyebrow={t('brand_detail.where_to_find')}>
          <WebsitePill url={socials?.website} />
          <div className="mt-3">
            <DTSocials socials={{ ...socials, website: null }} size={36} />
          </div>
        </SidebarCard>
      )}

      {relevantLinks.length > 0 && (
        <SidebarCard eyebrow={t('brand_detail.relevant_links')}>
          <ul className="mt-3 space-y-2">
            {relevantLinks.map((l, i) => (
              <li key={i}>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 text-[12.5px] text-ink-strong font-semibold hover:text-brand"
                >
                  <span className="truncate">{l.label}</span>
                  <ArrowRightIcon className="w-3 h-3 shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </SidebarCard>
      )}
    </aside>
  );

  return (
    <div className="pb-12 bg-gray-100 min-h-[calc(100vh-80px)]">
      {renderHeader()}

      <div className="mx-auto max-w-[1180px]">
        {brand?.is_translated && brand?.original_language && (
          <div className="flex items-center gap-2 mt-4 px-4 sm:px-6 lg:px-7">
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">
              <LanguageIcon className="w-3.5 h-3.5" />
              {t('event_detail.translated_from', {
                language:
                  i18n.language === 'pt'
                    ? { pt: 'Português', en: 'Inglês' }[brand.original_language]
                    : { pt: 'Portuguese', en: 'English' }[
                        brand.original_language
                      ],
              })}
            </span>
            <button
              type="button"
              onClick={() => setViewingOriginal((v) => !v)}
              className="text-xs text-yellow-600 hover:text-yellow-700 underline underline-offset-2"
            >
              {viewingOriginal
                ? t('event_detail.view_translated')
                : t('event_detail.view_original')}
            </button>
          </div>
        )}

        {/* Mobile-only invite/save action row — header cluster is hidden < lg */}
        {isPromoter && (
          <div className="lg:hidden px-4 sm:px-6 lg:px-7 mt-4 flex items-center gap-2">
            <InviteMenu
              onInvite={handleInvite}
              triggerLabel={t('brand_detail.invite_action')}
              triggerIcon={<PlusIcon className="w-3.5 h-3.5" />}
              triggerClassName="inline-flex items-center justify-between gap-2 px-3.5 h-10 rounded-full text-[12.5px] font-bold transition bg-yellow-03 text-white hover:bg-yellow-05 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            />
          </div>
        )}

        <div className="px-4 sm:px-6 lg:px-7 mt-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <div className="space-y-8 min-w-0">
            {renderPortfolio()}
            {renderAbout()}
            {renderCategories()}
            {renderUpcoming()}
            {renderPast()}
          </div>

          {renderSidebar()}
        </div>
      </div>

      {showInfoModal && (
        <ModalDialog
          isOpen={showInfoModal}
          closeModal={handleCloseMoreModal}
          title={modalContent.title}
          content={modalContent.content}
          size={modalContent.size}
          hideIcon
        />
      )}

      {showMediaModal && imagesForModal.length > 0 && (
        <CustomMediaModal
          isOpen={showMediaModal}
          onClose={() => setShowMediaModal(false)}
          images={imagesForModal}
          initialIndex={mediaStartIndex}
        />
      )}
    </div>
  );
};

export default BrandDetail;
